/**
 * Square Payment Form
 *
 * Renders Square Web Payment SDK form and handles tokenization.
 * Automatically detects sandbox vs production environment.
 * Includes comprehensive error tracking with @butter/monitoring.
 *
 * Implementation note: The container element (#square-card-container) must always
 * be rendered in the DOM before Square SDK initialization. Loading states are shown
 * as overlays to ensure the SDK can attach successfully.
 *
 * @module services/internalApi/payment/providers/square
 */

import { useEffect, useEffectEvent, useState, useRef, type SyntheticEvent } from "react";
import { withProviderErrorPrefix } from "@butter/library/shared";
import { reportClientPaymentError, addBreadcrumb } from "@butter/monitoring";
import { usePayment } from "../../context/usePayment";
import {
  detectSquareEnvironment,
  getSquareSdkUrl,
} from "~/lib/utils/paymentProvider";
import type { PaymentSettings } from "@lgc/database/types";

interface SquarePaymentFormProps {
  /** Club payment settings containing Square configuration */
  paymentSettings: PaymentSettings;
  /** Callback when payment token is successfully created. Can be async — form stays in loading state until it resolves. */
  onSuccess?: (token: string) => void | Promise<void>;
  /** Callback when an error occurs */
  onError?: (error: string) => void;
  /**
   * Notifies the host while the save is in flight — `true` from submit until BOTH
   * `tokenize()` and the awaited `onSuccess` persist have settled. Mirrors
   * `StripePaymentElementForm`'s prop of the same name.
   *
   * Hosts MUST use this to block navigation for the duration: a Square nonce is
   * single-use, so unmounting mid-persist strands it exactly as a spent SetupIntent
   * would — attached at Square, absent from our DB.
   */
  onSubmittingChange?: (submitting: boolean) => void;
  /** `id` attribute on the `<form>`, so an external button can `form={formId}` submit it. */
  formId?: string;
  /** Hide the internal submit button — for callers rendering their own CTA via `formId`. */
  hideSubmitButton?: boolean;
  /** Fires with whether the card is attached — `false` on init failure and on unmount. */
  onReadyChange?: (ready: boolean) => void;
}

/**
 * Square Web SDK types
 */
interface SquarePayments {
  card: () => Promise<SquareCard>;
}

interface SquareCard {
  attach: (element: HTMLElement) => Promise<void>;
  tokenize: () => Promise<SquareTokenResult>;
  addEventListener: (
    event: string,
    callback: (event: SquareCardEvent) => void
  ) => void;
  destroy: () => void;
}

interface SquareCardEvent {
  detail: {
    brand?: string;
    message?: string;
    type?: string;
    code?: string;
  };
}

interface SquareTokenResult {
  status: "OK" | "ERROR";
  token?: string;
  /** Square returns a machine-readable `code` alongside the human `detail`. */
  errors?: Array<{ detail: string; code?: string }>;
}

declare global {
  interface Window {
    Square?: {
      payments: (
        applicationId: string,
        locationId: string
      ) => Promise<SquarePayments>;
    };
    __SQUARE_LOADED__?: boolean;
    __SQUARE_LOAD_ERROR__?: boolean;
  }
}

/**
 * Square Payment Form
 *
 * Loads Square Web SDK and renders card payment form.
 * Automatically handles sandbox/production environments.
 *
 * @example
 * ```tsx
 * <SquarePaymentForm
 *   paymentSettings={club.settings?.paymentSettings}
 *   onSuccess={(token) => console.log('Token:', token)}
 *   onError={(error) => console.error('Error:', error)}
 * />
 * ```
 */
export function SquarePaymentForm({
  paymentSettings,
  onSuccess,
  onError,
  onSubmittingChange,
  formId,
  hideSubmitButton,
  onReadyChange,
}: SquarePaymentFormProps) {
  const { state, setIsProcessing } = usePayment();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  // Initialize from the live global so a pre-loaded SDK doesn't require a
  // setState-in-effect (which would cascade-render on mount).
  const [isSDKLoaded, setIsSDKLoaded] = useState<boolean>(
    () => typeof window !== "undefined" && !!window.Square
  );
  const [isCardReady, setIsCardReady] = useState(false);

  const cardRef = useRef<SquareCard | null>(null);
  const paymentsRef = useRef<SquarePayments | null>(null);
  const isSubmittingRef = useRef(false);

  // Held in a ref so the unmount cleanup below doesn't re-run on identity changes.
  const onSubmittingChangeRef = useRef(onSubmittingChange);
  onSubmittingChangeRef.current = onSubmittingChange;

  // Defence in depth: release the host's busy lock if we're unmounted mid-flight.
  // A stuck lock would leave the page permanently un-navigable — a worse failure
  // than the navigation it exists to prevent. Mirrors StripePaymentElementForm.
  useEffect(() => () => onSubmittingChangeRef.current?.(false), []);

  // Report readiness from state, never from the async init: a card that finishes
  // attaching after this form unmounted sets no state (a no-op), so it can't tell
  // the host "ready" for a form that no longer exists. `useEffectEvent` reads the
  // latest callback without making it a dependency. Mirrors StripePaymentElementForm.
  const reportReady = useEffectEvent((ready: boolean) => onReadyChange?.(ready));
  useEffect(() => {
    reportReady(isCardReady);
  }, [isCardReady]);
  useEffect(() => () => reportReady(false), []);

  // Validate Square configuration
  const squareConfig = paymentSettings?.square;
  const applicationId = squareConfig?.applicationId || "";
  const locationId = squareConfig?.locationId || "";
  // Default to 'sandbox' for type safety - this value is only used when config is invalid
  // and we show an error message instead of initializing the SDK
  const environment = applicationId ? detectSquareEnvironment(applicationId) : "sandbox";
  
  // Check if Square config is valid
  const isSquareConfigValid = !!(squareConfig && applicationId && locationId);

  // Config validity is derived from props, so surface its error during render
  // rather than via setState in an effect (which cascade-renders). Any stateful
  // error (SDK load / tokenize) takes precedence over the static config message.
  const configError = isSquareConfigValid
    ? null
    : "There is an issue with payment configuration. Please contact the club for help.";
  const displayError = errorMessage ?? configError;

  // Debug logging
  useEffect(() => {
    if (!isSquareConfigValid) {
      console.warn('[Square] Configuration validation failed:', {
        hasPaymentSettings: !!paymentSettings,
        hasSquareConfig: !!squareConfig,
        hasApplicationId: !!applicationId,
        hasLocationId: !!locationId,
      });
    }
  }, [isSquareConfigValid, paymentSettings, squareConfig, applicationId, locationId]);

  /**
   * Load Square Web SDK
   */
  useEffect(() => {
    // Invalid config is derived from props and surfaced during render (see
    // `configError`) — bail out of SDK loading without setting state here.
    if (!isSquareConfigValid) {
      return;
    }

    const sdkUrl = getSquareSdkUrl(applicationId);

    // SDK already present — `isSDKLoaded` was initialized from it, so just skip
    // loading. (No setState here: it would cascade-render.)
    if (window.Square) {
      return;
    }

    // Check if script is already in the DOM
    const existingScript = document.querySelector(`script[src="${sdkUrl}"]`);
    if (existingScript) {
      existingScript.addEventListener("load", () => {
        setIsSDKLoaded(true);
      });
      return;
    }

    // Load SDK script
    const script = document.createElement("script");
    script.src = sdkUrl;
    script.async = true;
    script.onload = () => {
      addBreadcrumb("payment", "Square SDK loaded", {
        environment,
        applicationId: applicationId.substring(0, 10) + "...",
      });
      setIsSDKLoaded(true);
    };
    script.onerror = () => {
      const message = "Failed to load Square payment SDK";
      console.error("[Square]", message);
      setErrorMessage(message);
      // Route SDK-load failures through the shared reporter so this surface
      // alerts consistently with admin/mobile's `load_sdk` reporting.
      reportClientPaymentError(
        { error: message, code: "sdk_load_failed", isInfrastructureError: true },
        {
          surface: "web_add_payment_method",
          operation: "load_sdk",
          provider: "square",
          tags: { environment },
        }
      );
    };

    document.body.appendChild(script);

    // Cleanup: destroy card instance on unmount
    return () => {
      if (cardRef.current) {
        try {
          cardRef.current.destroy();
        } catch (err) {
          console.error("[Square] Error destroying card:", err);
        }
      }
      // Don't remove script - leave it for other components
    };
  }, [isSquareConfigValid, applicationId, locationId, environment]);

  /**
   * Initialize Square card form
   * CRITICAL: Must wait for DOM element to exist before initializing
   */
  useEffect(() => {
    if (!isSDKLoaded || !window.Square) {
      return;
    }

    async function initializeCard() {
      try {
        // CRITICAL: Wait for DOM element to exist before attaching
        const element = document.getElementById("square-card-container");
        if (!element) {
          // Retry after a short delay to handle race conditions
          setTimeout(() => {
            const retryElement = document.getElementById(
              "square-card-container"
            );
            if (retryElement) {
              initializeCard();
            } else {
              // Retry failed - set error state and stop loading
              const errorMsg =
                "Payment form container not found. Please refresh and try again.";
              console.error("[Square] Container element not found after retry");
              setErrorMessage(errorMsg);
              setIsCardReady(false);

              // A rendering failure, but still an add-payment-surface failure —
              // report it in the same vocabulary so Sentry filters catch it.
              reportClientPaymentError(
                {
                  error: "Square container element not found after retry",
                  code: "container_missing",
                  isInfrastructureError: true,
                },
                {
                  surface: "web_add_payment_method",
                  operation: "initialize_card_retry",
                  provider: "square",
                  tags: { environment },
                }
              );
            }
          }, 100);
          return;
        }

        // Initialize Square Payments and card
        const payments = await window.Square!.payments(
          applicationId,
          locationId
        );
        paymentsRef.current = payments;

        const card = await payments.card();
        await card.attach(element);
        cardRef.current = card;

        addBreadcrumb("payment", "Square card form initialized", {
          environment,
        });

        setIsCardReady(true);
      } catch (error) {
        const message = "Failed to initialize Square card form";
        console.error("[Square] Initialization error:", error);
        setErrorMessage(message);
        setIsCardReady(false);
        reportClientPaymentError(
          { error: message, code: "init_failed", isInfrastructureError: true },
          {
            surface: "web_add_payment_method",
            operation: "initialize_card",
            provider: "square",
            tags: { environment },
            // Real thrown error → keep the Square SDK's stack.
            cause: error,
            context: {
              applicationId: applicationId.substring(0, 10) + "...",
              locationId: locationId.substring(0, 10) + "...",
            },
          }
        );
      }
    }

    initializeCard();
  }, [isSDKLoaded, applicationId, locationId, environment]);

  /**
   * Handle form submission and tokenize card
   */
  const handleSubmit = async (event: SyntheticEvent<HTMLFormElement>) => {
    event.preventDefault();

    // Synchronous double-click guard — closes the race window before React re-renders
    if (isSubmittingRef.current) return;

    if (!cardRef.current) {
      setErrorMessage("Payment form not ready. Please try again.");
      return;
    }

    isSubmittingRef.current = true;
    setIsProcessing(true);
    onSubmittingChange?.(true);
    setErrorMessage(null);

    addBreadcrumb("payment", "Square payment form submitted", {
      hasEmail: !!state.email,
      environment,
    });

    try {
      // Tokenize card
      const result = await cardRef.current.tokenize();

      if (result.status === "OK" && result.token) {
        // Success!
        addBreadcrumb("payment", "Square token created", {
          tokenLength: result.token.length,
          environment,
        });

        // Token flows straight to the caller via onSuccess; not stored in context.
        await onSuccess?.(result.token);
      } else {
        // Handle errors
        const errors = result.errors || [];
        // Square's own message, labelled so it reads as the provider's; generic
        // default when Square returned no detail.
        const message =
          errors.length > 0
            ? withProviderErrorPrefix("square", errors[0].detail)
            : "Failed to process payment. Please check your card details.";

        setErrorMessage(message);

        // A resolved non-OK result is a card-validation error (expected) —
        // recorded but quiet (`warning`), never a fake-amount critical. Pass Square's
        // own error code through: it makes the helper derive `card_error` rather than
        // `incomplete`, so a mistyped card stays distinguishable from an abandoned
        // 3DS / Financial Connections. (Falls back to `incomplete` when Square gives
        // us no code, which is the honest bucket for "something didn't complete".)
        reportClientPaymentError(
          { error: message, code: errors[0]?.code },
          {
            surface: "web_add_payment_method",
            operation: "tokenize",
            provider: "square",
            tags: { environment },
          }
        );

        onError?.(message);
      }
    } catch (error) {
      // A thrown tokenize is the infra seam — SDK couldn't reach Square
      // (connectivity / blocked endpoint), or the SDK itself broke (an incomplete
      // form once surfaced "Cannot read properties of undefined (reading
      // 'tokenizeFn')"). The raw text goes to monitoring, never on screen.
      const raw =
        error instanceof Error
          ? error.message
          : "An unexpected error occurred while processing payment.";

      setErrorMessage("We couldn't save your card. Check every card field, including the ZIP or postal code, and try again.");

      // Alerts (`error`).
      reportClientPaymentError(
        { error: raw, isInfrastructureError: true },
        {
          surface: "web_add_payment_method",
          operation: "tokenize",
          provider: "square",
          tags: { environment },
          // Real thrown error → keep the Square SDK's stack.
          cause: error,
        }
      );

      onError?.(raw);
    } finally {
      setIsProcessing(false);
      onSubmittingChange?.(false);
      isSubmittingRef.current = false;
    }
  };

  // Show error state if initialization failed and card is not ready
  if (displayError && !isCardReady) {
    return (
      <div className="space-y-4">
        <div className="rounded-md bg-red-50 border border-red-200 p-4">
          <p className="text-sm text-red-800 font-medium mb-2">
            Payment form error
          </p>
          <p className="text-sm text-red-700">{displayError}</p>
          <p className="text-xs text-red-600 mt-2">
            Please contact support if this issue persists.
          </p>
        </div>
      </div>
    );
  }

  return (
    <form id={formId} onSubmit={handleSubmit} className="space-y-4">
      {/* Environment indicator (dev/staging only) */}
      {import.meta.env.VITE_ENVIRONMENT !== "production" && (
        <div className="rounded-md bg-yellow-50 border border-yellow-200 p-2">
          <p className="text-xs text-yellow-800">
            Sandbox Mode - Use test card: 4111 1111 1111 1111
          </p>
        </div>
      )}

      {/* Square Card Container */}
      <div className="relative">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Card information
        </label>
        <div
          id="square-card-container"
          className="rounded-lg border border-gray-300 p-3 min-h-[100px]"
        />

        {/* Loading overlay - shows while SDK loads or card initializes */}
        {(!isSDKLoaded || !isCardReady) && (
          <div 
          className="absolute inset-0 bg-white bg-opacity-90 flex items-center justify-center rounded-lg"
          data-testid="square-loading-overlay"
          >

            <div className="flex flex-col items-center space-y-2">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <div className="text-gray-500 text-sm">
                Loading payment form...
              </div>
              <div className="text-xs text-gray-400">
                {!isSDKLoaded
                  ? "Loading Square SDK..."
                  : "Initializing card form..."}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Error Message */}
      {displayError && (
        <div className="rounded-md bg-red-50 border border-red-200 p-3">
          <p className="text-sm text-red-800">{displayError}</p>
        </div>
      )}

      {/* Submit Button */}
      {!hideSubmitButton && (
        <button
          type="submit"
          disabled={!isCardReady || state.isProcessing}
          className="w-full px-4 py-3 bg-theme-600 text-interactive:bg-theme-700 text-white rounded-full font-medium disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors duration-200"
          data-testid="checkout-save-payment-method-btn"
        >
          {state.isProcessing ? "Processing..." : "Save Payment Method"}
        </button>
      )}
    </form>
  );
}
